import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layers, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import GoogleButton from "@/components/GoogleButton";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      nav("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Log in to your studio.">
      <GoogleButton label="Log in with Google" />
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-[#d3d3d3]" />
        <span className="label-tech">or</span>
        <div className="flex-1 h-px bg-[#d3d3d3]" />
      </div>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email" testid="login-email" type="email" value={email} onChange={setEmail} />
        <Field label="Password" testid="login-password" type="password" value={password} onChange={setPassword} />
        {error && <div data-testid="login-error" className="text-sm text-[#ff3b00] font-tech">{error}</div>}
        <button data-testid="login-submit" disabled={busy} className="w-full bg-[#ff3b00] text-white font-semibold py-3 flex items-center justify-center gap-2 hover:bg-[#e63500] disabled:opacity-60 transition-colors">
          {busy ? "Signing in…" : (<>Log in <ArrowRight size={16} /></>)}
        </button>
      </form>
      <p className="mt-6 label-tech opacity-70">No account? <Link to="/register" className="text-[#ff3b00]">Create one</Link></p>
    </AuthShell>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#f2f0eb]">
      <div className="hidden md:flex flex-col justify-between p-12 bg-[#0a2540] text-white relative overflow-hidden">
        <div className="absolute inset-0 blueprint-grid opacity-20" />
        <Link to="/" className="relative flex items-center gap-2 font-heading font-black text-xl">
          <div className="w-7 h-7 bg-[#ff3b00] flex items-center justify-center"><Layers size={16} /></div>
          BuildForged
        </Link>
        <div className="relative">
          <h2 className="font-heading font-black text-4xl leading-tight tracking-tight">Build sites with AI.<br />Hand them to clients.</h2>
          <p className="mt-4 text-white/60 max-w-sm">The studio for agencies who want to ship fast and hand off cleanly.</p>
        </div>
        <div className="relative label-tech text-white/40">/ AI WEBSITE STUDIO</div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <h1 className="font-heading font-black text-3xl tracking-tight">{title}</h1>
          <p className="text-[#475569] mt-2 mb-8">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export function Field({ label, testid, type = "text", value, onChange }) {
  return (
    <div>
      <label className="label-tech block mb-1.5">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        className="w-full bg-white border border-[#d3d3d3] p-3 outline-none focus:border-[#ff3b00] focus:ring-2 focus:ring-[#ff3b00]/20"
      />
    </div>
  );
}
